import {inject, Injectable} from '@angular/core';
import {IJob} from '@service-and-repairs/awpintegrationlib';
import PQueue from 'p-queue';
import {ServiceCase} from '../models/service-case';
import {ServiceCaseService} from './service-case.service';
import {TaskQueue} from '../models/task-queue';

/**
 * A FIFO queue for sequential processing of service case updates.
 *
 * For each update, a request is sent to the backend API and the response is awaited before processing the next item in
 * the queue. When adding a service case update to the queue, any existing update with the same external service case
 * ID will be removed.
 *
 * This approach has two advantages.
 *
 * 1. Fewer requests will be sent to the backend, even when the user quickly adds a lot of items to
 * the service case in a very short time.
 *
 * 2. The UI can modify service cases with no perceived delay.
 *
 * The downside is the potential delay between the user initiating an update and learning about its definitive outcome.
 */
@Injectable({
  providedIn: 'root'
})
export class ServiceCaseUpdateQueueService {
  private readonly serviceCaseService: ServiceCaseService = inject(ServiceCaseService);

  private readonly queue = new PQueue({
    queueClass: TaskQueue,
    concurrency: 1
  });

  public addMetadataUpdate(serviceCaseUpdate: ServiceCase): Promise<ServiceCase> {
    return this.queue.add(
      (): Promise<ServiceCase> => this.serviceCaseService.updateMetadataInServiceCase(serviceCaseUpdate),
      {id: serviceCaseUpdate.getExternalId()}
    );
  }

  public addJobUpdate(serviceCase: ServiceCase, job: IJob): Promise<IJob> {
    return this.queue.add(
      (): Promise<IJob> => this.serviceCaseService.updateJobInServiceCase(serviceCase, job),
      {id: job.getExternalId()}
    );
  }

  public addJobDeletion(serviceCase: ServiceCase, jobId: string): Promise<void> {
    return this.queue.add(
      (): Promise<void> => this.serviceCaseService.deleteJobInServiceCase(serviceCase, jobId),
      {id: jobId}
    );
  }

  public hasMoreWithSameId(serviceCaseId: string): boolean {
    return this.queue.sizeBy({id: serviceCaseId}) > 0;
  }
}
